<script setup lang="ts">
import type { FormKitFrameworkContext } from '@formkit/core'
import FileUpload from 'vue-upload-component'
import { Sortable } from 'sortablejs-vue3'

const formkitProps = defineProps<{
  context: FormKitFrameworkContext
}>()
const props = computed(() => formkitProps.context?.attrs?.props || {})

const multiple = ref(!!props.value.multiple)
const files = ref([])
const accept = computed(
  () =>
    props.value.accept ||
    'image/png,image/gif,image/jpeg,image/webp,text/csv,application/pdf',
)
const extensions = computed(
  () => props.value.extensions || 'gif,jpg,jpeg,png,webp,pdf',
)
const directory = ref(false)
const drop = ref(true)
const dropDirectory = ref(true)
const createDirectory = ref(false)
const addIndex = ref(false)
const thread = ref(3)
const postAction = ref('/upload/post')
const putAction = ref('/upload/put')
const headers = ref({})
const data = ref({})

const formNode = shallowRef<any>(null)
const overlayOpened = ref(false)

const { validateImage } = useImageValidation()
const imageConstraints = computed(() => props.value.imageConstraints || null)
const validationErrors = ref<string[]>([])

const listRef = ref<HTMLElement>(null)
const uploadRef = ref(null)
const form = ref<{
  id?: number
  thumb?: any
  name?: string
  _id?: string
}>({})
const sortableOptions = {
  animation: 150,
}
const name = ref(`file-${formkitProps.context.id}`)

const nonImageTypes = [
  'pdf',
  'PDF',
  'csv',
  'CSV',
  'skp',
  'zip',
  'rar',
  '7z',
  'doc',
  'docx',
  '3D',
  'MAX',
  'DWG',
  'FBX',
  'fbx',
  '3ds',
  'dwg',
  'obj',
  '3ds',
  'mp4',
  'webm',
]

onMounted(() => {
  const value = formkitProps.context._value
  const initFiles = (
    Array.isArray(value) ? value : value?.file || value?.image ? [value] : []
  )
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(({ file, image, ...item }) => {
      const fileData = file || image
      return {
        data: {
          ...item,
        },
        id: fileData?.id,
        thumb: nonImageTypes.some((nit) => fileData?.fileUrl?.endsWith(nit))
          ? '/file.png'
          : fileData?.fileUrl,
        name:
          item.name ||
          fileData?.filename ||
          fileData?.fileUrl?.split('/')?.at(-1),
      }
    })

  uploadRef.value.add(initFiles)
})

const inputFiles = () => {
  const items = files.value.map(({ file, data, ...rest }, index) =>
    file instanceof File
      ? {
          ...data,
          file,
          sortOrder: index + 1,
        }
      : {
          ...data,
          file: {
            fileUrl: rest.thumb,
            id: rest.id,
          },
          sortOrder: index + 1,
          name: props.value.filename ? rest.name : undefined,
        },
  )

  formkitProps.context.node.input(multiple.value ? items : items.at(0))
}

const inputFilter = async (newFile, oldFile, prevent) => {
  if (
    newFile &&
    newFile.error === '' &&
    newFile.file &&
    (!oldFile || newFile.file !== oldFile.file)
  ) {
    if (imageConstraints.value && newFile.file.type?.startsWith('image/')) {
      const result = await validateImage(newFile.file, imageConstraints.value)
      if (!result.isValid) {
        validationErrors.value = result.errors
        prevent()
        return false
      }
    }
    validationErrors.value = []
    // Create a blob field
    newFile.blob = ''
    const URL = window.URL || window.webkitURL
    if (URL) {
      newFile.blob = URL.createObjectURL(newFile.file)
    }
    // Thumbnails
    newFile.thumb = ''
    if (newFile.blob && newFile.type.substr(0, 6) === 'image/') {
      newFile.thumb = newFile.blob
    }
  }
}

const removeFile = (file) => {
  files.value = files.value.filter((item) => item.id !== file.id)
  validationErrors.value = []

  inputFiles()
}

const onSaveItem = async () => {
  const { _id, name, thumb, ...data } = form.value
  const index = files.value?.findIndex((item) => item.id === _id)

  if (props.value.singleKey) {
    files.value.forEach((file, i) => {
      if (i !== index) {
        Object.keys(data).forEach((key) => {
          if (data[key] === true) {
            file.data[key] = false
          }
        })
      }
    })
  }

  files.value[index] = {
    ...files.value[index],
    id: _id,
    name,
    thumb,
    data,
  }

  inputFiles()
}

const onOpenFile = (file) => {
  form.value = {
    ...file.data,
    _id: file.id,
    thumb: file.thumb,
    name: file.name,
  }

  overlayOpened.value = true
}

const onReorderFiles = ({ oldIndex, newIndex }) => {
  const item = files.value.splice(oldIndex, 1).at(0)
  files.value.splice(newIndex, 0, item)

  inputFiles()
}

const clearUploadsList = () => {
  files.value = []
  validationErrors.value = []
  inputFiles()
}
</script>

<template>
  <div class="example-full flex flex-col">
    <div
      class="upload bg-gray-50 p-4 border rounded-lg"
      :class="{
        'border-red-500':
          context.node.props.validation?.includes('required') &&
          !context.state.valid,
      }"
    >
      <div
        v-if="multiple || !files.length"
        class="text-center pt-4 text-gray-700"
        :class="files.length ? 'pb-6' : 'pb-4'"
      >
        <span>Drag & Drop your files or </span>
        <label
          :for="name"
          class="underline cursor-pointer hover:text-primary-600"
        >
          Browse
        </label>
      </div>
      <div
        v-if="validationErrors.length"
        class="bg-red-50 border border-red-200 rounded p-3 mb-3"
      >
        <div class="text-red-800 font-semibold mb-1">Upload rejected:</div>
        <ul class="text-red-700 text-sm list-disc list-inside">
          <li v-for="(error, index) in validationErrors" :key="index">
            {{ error }}
          </li>
        </ul>
      </div>
      <div ref="listRef">
        <Sortable
          :list="files"
          :options="sortableOptions"
          item-key="id"
          class="grid min-h-10"
          :class="{
            'md:grid-cols-5 gap-3': multiple,
            'min-h-[2.5rem]': files.length,
          }"
          @end="onReorderFiles"
        >
          <template #item="{ element: file, index }">
            <div
              :key="file.id"
              class="relative flex justify-center items-center overflow-hidden bg-black rounded-lg ring-1 ring-black ring-opacity-5 shadow-sm cursor-pointer group hover:ring-primary first:ring-primary first:ring-2"
              :class="multiple ? 'aspect-square' : 'max-h-[18rem]'"
              @click="onOpenFile(file)"
            >
              <div
                class="flex flex-col p-2 absolute w-full h-20 top-0 left-0 bg-gradient-to-b from-black/80 via-black/50 space-x-1"
              >
                <div class="flex filename">
                  <button
                    class="flex justify-center items-center w-6 h-6 bg-black/50 rounded-full ring-white hover:ring-2 flex-shrink-0 mr-1"
                    @click.stop.prevent="removeFile(file)"
                  >
                    <svg
                      class="h-6 w-6 text-white"
                      viewBox="0 0 26 26"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.586 13l-2.293 2.293a1 1 0 0 0 1.414 1.414L13 14.414l2.293 2.293a1 1 0 0 0 1.414-1.414L14.414 13l2.293-2.293a1 1 0 0 0-1.414-1.414L13 11.586l-2.293-2.293a1 1 0 0 0-1.414 1.414L11.586 13z"
                        fill="currentColor"
                        fill-rule="nonzero"
                      />
                    </svg>
                  </button>
                  <div
                    class="flex items-center text-gray-300 text-xs h-6 overflow-hidden group-hover:text-white"
                  >
                    <span class="truncate">{{ file.name }}</span>
                  </div>
                </div>
                <div class="flex flex-col items-start gap-1 mt-1">
                  <div
                    v-for="key in Object.keys(file.data).filter(
                      (k) => file.data[k] === true,
                    )"
                    :class="`text-[12px] font-bold text-white rounded px-1 ${props.dataKeysStyle[key]}`"
                  >
                    {{
                      key === 'noImage'
                        ? 'No Image'
                        : key.replaceAll('is', '') || ''
                    }}
                  </div>
                </div>
              </div>
              <img
                class="block object-contain object-center max-h-full"
                :src="file.thumb"
              />
            </div>
          </template>
        </Sortable>
      </div>
      <div>
        <file-upload
          ref="uploadRef"
          v-model="files"
          class="!hidden"
          :post-action="postAction"
          :put-action="putAction"
          :extensions="extensions"
          :accept="accept"
          :multiple="multiple"
          :directory="directory"
          :create-directory="createDirectory"
          :thread="thread < 1 ? 1 : thread > 5 ? 5 : thread"
          :headers="headers"
          :data="data"
          :drop="drop"
          :drop-directory="dropDirectory"
          :add-index="addIndex"
          :input-id="name"
          @input-filter="inputFilter"
          @input-file="inputFiles"
        />
      </div>
    </div>
    <Overlay v-model="overlayOpened" title="File Edit" @save="onSaveItem">
      <div class="space-y-4">
        <div
          class="relative flex justify-center items-center aspect-square overflow-hidden bg-black rounded-lg ring-1 ring-black ring-opacity-5 shadow-sm"
        >
          <img
            class="block object-contain object-center max-h-full"
            :src="form.thumb"
          />
        </div>
        <div>
          <a :href="form.thumb" target="_blank">
            <h2 class="text-lg font-medium text-gray-900 truncate">
              {{ form.name }}
            </h2>
          </a>
          <FormKit
            v-if="props.schema"
            :id="`image-${form.id}`"
            v-model="form"
            type="form"
            :actions="false"
            @node="formNode = $event"
          >
            <FormKitSchema :schema="props.schema" />
          </FormKit>
        </div>
      </div>
    </Overlay>
    <Button
      v-if="multiple"
      class="text-gray-400 hover:text-red-800 hover:bg-red-100 mt-2"
      @click="clearUploadsList"
    >
      <div class="i-heroicons-outline-trash" />
      <span>Delete All</span>
    </Button>
  </div>
</template>

<style lang="postcss" scoped>
.sortable-ghost {
  @apply opacity-60;
}
</style>
